/**
 * Deck Card Details Panel State
 *
 * Manages which card is selected for viewing in the deck editor details panel.
 * Supports two modes:
 * - 'preview': Viewing a card from search results (not yet in deck)
 * - 'in-deck': Viewing a card that's already in the deck
 */
import { atom } from 'jotai';

// ============================================================================
// Types
// ============================================================================

export type CardDetailsMode = 'preview' | 'in-deck';

export interface DeckCardDetailsState {
  /** Scryfall ID of the card to display (null = panel closed) */
  scryfallId: string | null;
  /** Mode: preview from search, or in-deck for cards in the deck */
  mode: CardDetailsMode;
  /** DeckCard document ID when mode is 'in-deck' (for quantity/zone operations) */
  deckCardId: string | null;
}

// ============================================================================
// Atoms
// ============================================================================

/** State for the deck card details panel */
export const deckCardDetailsAtom = atom<DeckCardDetailsState>({
  scryfallId: null,
  mode: 'preview',
  deckCardId: null,
});

/** Derived atom: is the panel open? */
export const isCardDetailsPanelOpenAtom = atom((get) => get(deckCardDetailsAtom).scryfallId !== null);

// ============================================================================
// Actions
// ============================================================================

/** Open details panel for a card in search results (preview mode) */
export const openCardPreviewAtom = atom(null, (_get, set, scryfallId: string) => {
  set(deckCardDetailsAtom, {
    scryfallId,
    mode: 'preview',
    deckCardId: null,
  });
});

/** Open details panel for a card in the deck */
export const openDeckCardDetailsAtom = atom(null, (_get, set, { scryfallId, deckCardId }: { scryfallId: string; deckCardId: string }) => {
  set(deckCardDetailsAtom, {
    scryfallId,
    mode: 'in-deck',
    deckCardId,
  });
});

/** Close the details panel */
export const closeCardDetailsAtom = atom(null, (_get, set) => {
  set(deckCardDetailsAtom, {
    scryfallId: null,
    mode: 'preview',
    deckCardId: null,
  });
});
